import { Button } from '@unistocks/components/button/button';
import { CalendarLevel, DateStringValue } from '@unistocks/components/calendar/types';
import { hasPreventDefault } from '@unistocks/components/calendar/utils/has-prevent-default';
import { DatePickerType, DatePickerValue } from '@unistocks/components/date-picker/types';
import { Row } from '@unistocks/components/layout/flex';
import { withFeedback } from '@unistocks/shared/utils/haptics/with-feedback';
import { arrayify } from '@unistocks/shared/utils/util';

/** A predefined value the user can jump to from a picker's preset list. */
export interface PickerPreset<Type extends DatePickerType> {
  /** Value applied when the preset is selected (shaped by the picker `type`). */
  value: DatePickerValue<Type, DateStringValue>;

  /** Preset label. */
  label: React.ReactNode;
}

interface PresetListProps<Type extends DatePickerType> {
  /** Predefined values to render. */
  presets: PickerPreset<Type>[];

  /** Imperative setter for the Calendar's displayed date (from `__setDateRef`). */
  setDateRef: React.RefObject<((date: DateStringValue) => void) | null>;

  /** Imperative setter for the Calendar's displayed level (from `__setLevelRef`). */
  setLevelRef: React.RefObject<((level: CalendarLevel) => void) | null>;

  /** Level the list resets the Calendar to on select (`month`/`year`/`decade`). */
  level: CalendarLevel;

  /** Applies the selected value (the picker's `setValue`). */
  setValue: (value: DatePickerValue<Type, DateStringValue>) => void;

  /** If defined, called with the preset value INSTEAD of `setValue`. */
  __onPresetSelect?: (preset: DatePickerValue<Type, DateStringValue>) => void;
}

/**
 * Renders the shared inline-picker preset list — a `role="list"` of pressable
 * presets that jump the Calendar's date + level, then apply the value (or defer
 * to `__onPresetSelect`). Rendered as a component so the imperative refs are
 * only touched from the press handlers, never during render.
 */
export function PresetList<Type extends DatePickerType>({
  presets,
  setDateRef,
  setLevelRef,
  level,
  setValue,
  __onPresetSelect
}: PresetListProps<Type>) {
  const handlePresetSelect = (presetValue: DatePickerValue<Type, DateStringValue>) => {
    const view: DatePickerValue<DatePickerType, DateStringValue> = presetValue;
    const firstDate = Array.isArray(view) ? view[0] : view;
    if (firstDate === undefined) {
      return;
    }
    if (firstDate !== null) {
      setDateRef.current?.(firstDate);
    }
    setLevelRef.current?.(level);
    if (__onPresetSelect) {
      __onPresetSelect(presetValue);
    } else {
      setValue(presetValue);
    }
  };

  return (
    <Row role="list" w={'100%'} mt={'$3'}>
      {presets.map(({ label, value }) => {
        const key = arrayify(value).join('-');

        return (
          <Button
            key={key}
            variant={'outlined'}
            size={'$2'}
            onPress={() => handlePresetSelect(value)}
            // Compose the haptic here: an explicit `onPressIn` prop wins over the
            // button's `withFeedback` variant, so the preset fires it itself.
            onPressIn={event => {
              withFeedback();
              if (hasPreventDefault(event)) {
                event.preventDefault();
              }
            }}
          >
            {label}
          </Button>
        );
      })}
    </Row>
  );
}
